import styled from '@emotion/styled';
import { Configuration, Model, ModelStatus } from '@gameonly/core';
import EditIcon from '@mui/icons-material/Edit';
import { IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { authenticatedFetch } from '../authentication/authenticatedFetch';
import NavComponent from '../results/Nav';
import PdfDownloadDialog, {
  PdfDownloadContext,
} from '../results/PdfDownloadDialog';
import {
  getNav,
  ResultDispatch,
  StickyHeader,
  StickyWrapper,
} from '../results/Results';
import Block, { TitleBlock } from '../ui/Block';
import { PageContainer } from '../ui/Container';
import { ReactComponent as ExportIcon } from '../ui/icons/export.svg';
import CustomLoadingButton from '../ui/LoadingButton';
import useReport from './context/useReport';

const ReportTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
  justify-content: center;
`;

export const annualReportModel: Model = {
  name: 'annual-reports',
  _id: {} as Model['_id'],
  hasValidation: false,
  hasRequiredFields: false,
  singularName: '',
  type: '',
  color: '',
  description: '',
  spreadsheetId: '',
  parameters: [],
  config: {} as Configuration,
  defaultLanguage: '',
  changelog: '',
  userInformation: '',
  status: ModelStatus.DRAFT,
  publishedAt: new Date(),
  createdAt: new Date(),
  updatedAt: new Date(),
  versionNumber: 0,
};

const AnnualReport = () => {
  const { reportId } = useParams();
  const { report, rename } = useReport(reportId);
  const { enqueueSnackbar } = useSnackbar();
  const { t, i18n } = useTranslation(['annual-reports']);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const pdfContext: PdfDownloadContext = {
    label: 'Choisir le type de rapport PDF',
    fetch: async (reportType) => {
      const query = `reportType=${reportType}&lang=${i18n.language}`;
      return authenticatedFetch(
        `${process.env.REACT_APP_API_URL}/annual-reports/${reportId}/pdf?${query}`,
      );
    },
    fileName: (reportType) =>
      `${report?.name.toLowerCase().replace(/\s/g, '-')}-${reportType}-${
        i18n.language
      }.pdf`,
  };

  if (!report) {
    return null;
  }

  const handleEditClicked = async () => {
    let newName = prompt('Renommer le rapport ?', report.name);
    if (!newName) return;
    try {
      await rename(newName);
      enqueueSnackbar('Le rapport a bien été renommé', {
        variant: 'success',
      });
    } catch (error) {
      enqueueSnackbar('Une erreur est survenue', {
        variant: 'error',
      });
    }
  };

  const nav = getNav(report.results, undefined);
  const reportResultsExceptNav = report.results.filter(
    (result) => result.type !== 'nav',
  );

  return (
    <PageContainer>
      <PdfDownloadDialog
        open={isDialogOpen}
        onClose={() => setDialogOpen(false)}
        context={pdfContext}
      />
      <TitleBlock background="var(--color-primary)">
        <ReportTitle>
          {report.name}
          <IconButton onClick={handleEditClicked}>
            <EditIcon />
          </IconButton>
        </ReportTitle>
        <p>{t('annual-reports:report.subtitle')}</p>

        <ButtonContainer>
          <CustomLoadingButton
            className="clear"
            onClick={() => setDialogOpen(true)}
            startIcon={<ExportIcon />}
          >
            Récupérer le rapport PDF
          </CustomLoadingButton>
        </ButtonContainer>
      </TitleBlock>
      <StickyWrapper
        top={64}
        paddingTop={16}
        backgroundColor="var(--backgroundColor)"
      >
        {nav && (
          <StickyHeader>
            <NavComponent result={nav} model={annualReportModel} />
          </StickyHeader>
        )}
      </StickyWrapper>
      <Block>
        {reportResultsExceptNav.map((result, index) => (
          <ResultDispatch
            result={result}
            key={index}
            model={annualReportModel}
          />
        ))}
      </Block>
    </PageContainer>
  );
};

export default AnnualReport;
